import React from "react";
import { Dialog } from "@sscs/ui";
import { LinkToReport } from "Components";
import { FileType } from "Types";
import { FileDuplicateCheck } from "../../hooks/useCheckFileDuplicates";
import styles from "./fileVerification.module.scss";

function splitPurl(purl: string): {
  projectName: string;
  packageName: string;
  version: string;
} {
  const [part1] = purl.split("?");
  const [, projectName, packageDetails] = part1.split("/");
  const [packageName, version] = packageDetails.split("@");

  return {
    projectName,
    packageName,
    version,
  };
}

const GIGABYTE = 1024 * 1024 * 1024;

type UploadWarningProps = {
  uploadedFile: File;
  uploadType: FileType;
  duplicateCheck: FileDuplicateCheck;
};

const FileVerification = ({
  uploadedFile,
  uploadType,
  duplicateCheck,
}: UploadWarningProps): React.JSX.Element | null => {
  const {
    data,
    isLoading: isLoadingFileDuplicates,
    isFetching: isFetchingFileDuplicates,
    isGeneratingHash,
    isSuccess,
    progress,
    hashingError,
  } = duplicateCheck;

  const { projectName, packageName, version } = data?.purl
    ? splitPurl(data.purl)
    : { projectName: undefined, packageName: undefined, version: undefined };

  const latestReportId = data?.latest_report_id ?? null;
  const fileAlreadyAnalyzed = latestReportId !== null;

  const isAnalyzing =
    isLoadingFileDuplicates || isFetchingFileDuplicates || isGeneratingHash;

  const hugeFileWarning = !!uploadedFile && uploadedFile.size >= GIGABYTE;

  const fileAnalyzingDurationMinutes = uploadedFile
    ? Math.ceil(uploadedFile.size / (GIGABYTE * 2))
    : 0;

  if (isAnalyzing) {
    const hashingStage = progress === 100 ? "finalizing" : "hashing";
    const title = isGeneratingHash
      ? `Verifying file... (${hashingStage})`
      : "Verifying file...";

    return (
      <Dialog.Banner type="progress" progress={progress} title={title}>
        We are checking your file for previous analysis results.{" "}
        {hugeFileWarning && (
          <>
            This might take {fileAnalyzingDurationMinutes.toLocaleString()}{" "}
            minute
            {fileAnalyzingDurationMinutes > 1 ? "s" : ""}.{" "}
          </>
        )}
        To skip the check, click <strong>Upload</strong>.
        {hashingError && (
          <div className={styles["error-message"]}>
            We can{`'`}t verify the file right now, but you can still proceed
            with the upload.
          </div>
        )}
      </Dialog.Banner>
    );
  }

  if (
    isSuccess &&
    fileAlreadyAnalyzed &&
    projectName &&
    packageName &&
    version
  ) {
    return (
      <Dialog.Banner type="warning" title="Warning: File already analyzed">
        Uploading this file will <strong>consume analysis capacity</strong>.
        Instead, you can view the{" "}
        <LinkToReport
          report={{
            projectName: projectName,
            packageName: packageName,
            version: version,
            isRepro: uploadType === "FILESTREAM" ? undefined : false,
          }}
        >
          latest report
        </LinkToReport>
        .
      </Dialog.Banner>
    );
  }

  if (isSuccess && !fileAlreadyAnalyzed) {
    return (
      <Dialog.Banner type="success" title="File verification done">
        File checked for previous analysis results. No previous analysis found.
      </Dialog.Banner>
    );
  }

  return null;
};

export default FileVerification;
